import { createKeyResult, createOKR, deleteOKR, deleteKeyResult, getMyOKRs, listKeyResultsByOKR, listOKRs, listOKRsByEmployee, updateKeyResult, updateOKR } from '@/api/performance';
import { getMyAllReports, listEmployees } from '@/api/workforce';
import { KRScoringDialog } from '@/components/performance/Scoring/KRScoringDialog';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import type { KeyResult, OKR } from '@/types/performance';
import type { Employee } from '@/types/workforce';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Edit,
  FileText,
  Plus,
  Star,
  Target,
  Trash2,
  TrendingUp,
  Users
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';

interface Meta {
  id: string;
  titulo: string;
  categoria: string;
  cargo: string;
}

const AvaliacaoIndividual = () => {
  const { tenantDomain } = useTenant();
  const { user } = useAuth();

  // Support deep-link via ?employeeId=xxx from objetivos-individuais detail page
  const urlEmployeeId = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('employeeId')
    : null;

  const [employeeSelecionado, setEmployeeSelecionado] = useState<string>(urlEmployeeId || '');
  const [employeeSelecionadoNome, setEmployeeSelecionadoNome] = useState<string>(''); // Store name when selected via autocomplete
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [metaSelecionada, setMetaSelecionada] = useState<string>('');
  const [expandedKRs, setExpandedKRs] = useState<Record<string, boolean>>({});
  const [realEmployees, setRealEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);

  // Check if user is admin, rh (HR director), analista-rh (HR Analyst), or diretor-tecnico
  const canCreateObjectives = user?.role && ['admin', 'rh', 'analista-rh', 'diretor-tecnico', 'coordenador'].includes(user.role) || false;

  // Check if user should see autocomplete search (executives + HR)
  const showAutocomplete = user?.role && ['admin', 'rh', 'analista-rh', 'ceo', 'vice-presidente'].includes(user.role) || false;

  // Create Objective dialog state
  const [isCreateOKRDialogOpen, setIsCreateOKRDialogOpen] = useState(false);
  const [objetivoOKR, setObjetivoOKR] = useState('');
  const [descricaoOKR, setDescricaoOKR] = useState('');
  const [categoriaOKR, setCategoriaOKR] = useState('');
  const [dataInicioOKR, setDataInicioOKR] = useState('');
  const [dataFimOKR, setDataFimOKR] = useState('');

  // Edit Objective dialog state
  const [isEditOKRDialogOpen, setIsEditOKRDialogOpen] = useState(false);
  const [editingOKRId, setEditingOKRId] = useState<string | null>(null);
  const [editObjetivoOKR, setEditObjetivoOKR] = useState('');
  const [editDescricaoOKR, setEditDescricaoOKR] = useState('');
  const [editCategoriaOKR, setEditCategoriaOKR] = useState('');
  const [editDataInicioOKR, setEditDataInicioOKR] = useState('');
  const [editDataFimOKR, setEditDataFimOKR] = useState('');

  // Estado do formulário de KR
  const [nomeKR, setNomeKR] = useState('');
  const [descricaoKR, setDescricaoKR] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [peso, setPeso] = useState('');


  // Edit KR dialog state
  const [isEditKRDialogOpen, setIsEditKRDialogOpen] = useState(false);
  const [editingKR, setEditingKR] = useState<{ krId: string; okrId: string } | null>(null);
  const [editNomeKR, setEditNomeKR] = useState('');
  const [editDescricaoKR, setEditDescricaoKR] = useState('');
  const [editDataInicioKR, setEditDataInicioKR] = useState('');
  const [editDataFimKR, setEditDataFimKR] = useState('');
  const [editPesoKR, setEditPesoKR] = useState('');

  // KR Scoring
  const [scoringKR, setScoringKR] = useState<KeyResult | null>(null);
  const [isScoringDialogOpen, setIsScoringDialogOpen] = useState(false);

  // OKRs (metas) from performance module - grouped by employee ID
  const [okrsPorEmployee, setOkrsPorEmployee] = useState<Record<string, OKR[]>>({});
  const [loadingOKRs, setLoadingOKRs] = useState(false);

  // Key Results from backend - grouped by OKR ID
  const [keyResultsByOKR, setKeyResultsByOKR] = useState<Record<string, KeyResult[]>>({});
  const [loadingKRs, setLoadingKRs] = useState(false);

  // Fetch employees on mount
  useEffect(() => {
    const fetchEmployees = async () => {
      // Wait for user to be loaded before fetching to avoid race conditions
      if (!tenantDomain || !user) return;

      // Determine if user should fetch all employees (HR + executives)
      const isHRUser = user.role && ['admin', 'rh', 'analista-rh', 'ceo', 'vice-presidente'].includes(user.role);

      try {
        setLoadingEmployees(true);

        let employeeList: Employee[] = [];

        if (isHRUser) {
          // Admin/HR: Fetch all employees (limit 300)
          const response = await listEmployees(tenantDomain, { limit: 300 });
          employeeList = response.items;
        } else {
          // Regular user: Fetch only reports
          const response = await getMyAllReports({ limit: 100 });
          employeeList = response.items;

          // Add current user first if they have employeeId
          if (user?.employee?.employeeId) {
            employeeList = [user.employee, ...response.items];
          }
        }

        setRealEmployees(employeeList);

        // If deep-linked with employeeId, resolve the name from the list
        if (urlEmployeeId && employeeList.length > 0) {
          const linked = employeeList.find(e => e.employeeId === urlEmployeeId);
          if (linked) {
            setEmployeeSelecionadoNome(linked.personalInfo?.name || '');
          }
        }

        // Set selected employee: prioritize current user if they have employeeId
        // For admin/RH users, don't auto-select - let them search and choose
        if (employeeList.length > 0 && !employeeSelecionado && !isHRUser) {
          let selectedEmployee = employeeList[0];

          // For regular users, default to current user if available
          if (user?.employee?.employeeId) {
            selectedEmployee = user.employee;
          }

          setEmployeeSelecionado(selectedEmployee.employeeId);
        }
      } catch (err: any) {
        console.error('Error fetching employees:', err);
        setRealEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [tenantDomain, user]);

  // Fetch OKRs when employee selection changes
  useEffect(() => {
    const fetchOKRs = async () => {
      if (!tenantDomain || !employeeSelecionado) return;

      // Skip if we already have OKRs for this employee
      if (okrsPorEmployee[employeeSelecionado]) return;

      try {
        setLoadingOKRs(true);

        const isViewingSelf = user?.employee?.employeeId === employeeSelecionado;

        let okrsForEmployee: OKR[];

        if (isViewingSelf) {
          // Self-service: use /okrs/me (no manager role required)
          const response = await getMyOKRs(tenantDomain, { limit: 100 });
          okrsForEmployee = response.items;
        } else {
          // Manager viewing a report's OKRs: use /okrs/employee/{id}
          try {
            const response = await listOKRsByEmployee(tenantDomain, employeeSelecionado, { limit: 100 });
            okrsForEmployee = response.items;
          } catch (byEmployeeErr: any) {
            // Fallback to listOKRs + client-side filter for backwards compatibility
            const response = await listOKRs(tenantDomain, {
              type: 'individual',
              limit: 100
            });
            okrsForEmployee = response.items.filter(
              okr => okr.ownerId === employeeSelecionado
            );
          }
        }

        setOkrsPorEmployee(prev => ({
          ...prev,
          [employeeSelecionado]: okrsForEmployee
        }));
      } catch (err: any) {
        console.error('Error fetching OKRs:', err);
        setOkrsPorEmployee(prev => ({
          ...prev,
          [employeeSelecionado]: []
        }));
      } finally {
        setLoadingOKRs(false);
      }
    };

    fetchOKRs();
  }, [tenantDomain, employeeSelecionado]);

  // Fetch Key Results for all OKRs when OKRs change
  useEffect(() => {
    const fetchKeyResults = async () => {
      if (!tenantDomain || !employeeSelecionado) return;

      const okrs = okrsPorEmployee[employeeSelecionado];
      if (!okrs || okrs.length === 0) return;

      try {
        setLoadingKRs(true);

        // Fetch Key Results for each OKR
        const krPromises = okrs.map(async (okr) => {
          try {
            const response = await listKeyResultsByOKR(tenantDomain, okr.okrId, { limit: 100 });
            return { okrId: okr.okrId, keyResults: response.keyResults };
          } catch (err) {
            console.error(`Error fetching KRs for OKR ${okr.okrId}:`, err);
            return { okrId: okr.okrId, keyResults: [] };
          }
        });

        const results = await Promise.all(krPromises);

        // Build map of OKR ID -> Key Results
        const krMap: Record<string, KeyResult[]> = {};
        results.forEach(({ okrId, keyResults }) => {
          krMap[okrId] = keyResults;
        });

        setKeyResultsByOKR(prev => ({
          ...prev,
          ...krMap
        }));
      } catch (err: any) {
        console.error('Error fetching Key Results:', err);
      } finally {
        setLoadingKRs(false);
      }
    };

    fetchKeyResults();
  }, [tenantDomain, employeeSelecionado, okrsPorEmployee]);


  // Use real employee names for responsaveis list
  const responsaveis = realEmployees.map(emp => emp.personalInfo.name);

  // Agrupar metas por categoria
  const getMetasAgrupadasPorCategoria = () => {
    const okrs = okrsPorEmployee[employeeSelecionado] || [];

    // Convert OKRs to Meta format
    const metas: Meta[] = okrs.map(okr => ({
      id: okr.okrId,
      titulo: okr.objective,
      categoria: okr.category || 'Sem Categoria',
      cargo: employeeSelecionado // Store employeeId here for backward compatibility
    }));

    const agrupadas: Record<string, Meta[]> = {};

    metas.forEach(meta => {
      if (!agrupadas[meta.categoria]) {
        agrupadas[meta.categoria] = [];
      }
      agrupadas[meta.categoria].push(meta);
    });

    return agrupadas;
  };

  // Ícones por categoria
  const getCategoriaIcon = (categoria: string) => {
    switch (categoria) {
      case 'Gestão de Eventos':
        return Calendar;
      case 'Gestão de Fornecedores':
        return Users;
      case 'Crescimento de Receita':
        return DollarSign;
      case 'Eficiência Operacional':
        return TrendingUp;
      case 'Excelência Criativa':
        return Target;
      default:
        return FileText;
    }
  };

  // Função para abrir dialog com meta selecionada
  const handleAdicionarKR = (metaId: string) => {
    setMetaSelecionada(metaId);
    // Auto-set responsável to the selected employee (owner of the OKR)
    const employeeName = employeeAtual?.personalInfo.name || employeeSelecionadoNome;
    if (employeeName) {
      setResponsavel(employeeName);
    }
    setIsDialogOpen(true);
  };

  // Função para salvar KR - now calls backend API
  const handleSalvarKR = async () => {
    if (!tenantDomain || !metaSelecionada) return;

    try {
      // Find the responsible employee's ID from name
      const responsibleEmployee = realEmployees.find(
        emp => emp.personalInfo.name === responsavel
      );

      // Create Key Result via API
      const newKR = await createKeyResult(
        tenantDomain,
        metaSelecionada, // okrId
        {
          okrId: metaSelecionada,
          name: nomeKR,
          description: descricaoKR,
          period: dataInicio && dataFim ? {
            startDate: dataInicio,
            endDate: dataFim
          } : undefined,
          weight: peso ? parseInt(peso, 10) : undefined,
          ownerId: responsibleEmployee?.employeeId,
          status: 'in-progress'
        }
      );

      // Update local state with new Key Result
      setKeyResultsByOKR(prev => ({
        ...prev,
        [metaSelecionada]: [...(prev[metaSelecionada] || []), newKR]
      }));

      // Limpar formulário
      setNomeKR('');
      setDescricaoKR('');
      setDataInicio('');
      setDataFim('');
      setResponsavel('');
      setPeso('');
      setIsDialogOpen(false);
    } catch (err: any) {
      console.error('Error creating Key Result:', err);
      toast.error('Erro ao criar Key Result. Por favor, tente novamente.');
    }
  };

  // Função para criar novo objetivo (OKR) - admin only
  const handleCriarObjetivo = async () => {
    if (!tenantDomain || !employeeSelecionado) return;

    // Validation
    if (!objetivoOKR || objetivoOKR.length < 10) {
      toast.error('O objetivo deve ter pelo menos 10 caracteres');
      return;
    }

    if (!dataInicioOKR || !dataFimOKR) {
      toast.error('Por favor, informe as datas de início e fim');
      return;
    }

    if (new Date(dataFimOKR) <= new Date(dataInicioOKR)) {
      toast.error('A data de fim deve ser posterior à data de início');
      return;
    }

    try {
      // Create OKR via API
      const newOKR = await createOKR(tenantDomain, {
        type: 'individual',
        ownerId: employeeSelecionado, // Using employee ID as owner (FIXED!)
        objective: objetivoOKR,
        description: descricaoOKR || undefined,
        category: categoriaOKR || undefined,
        period: {
          startDate: dataInicioOKR,
          endDate: dataFimOKR
        },
        status: 'active'
      });

      // Update local state with new OKR
      setOkrsPorEmployee(prev => ({
        ...prev,
        [employeeSelecionado]: [...(prev[employeeSelecionado] || []), newOKR]
      }));

      // Clear form
      setObjetivoOKR('');
      setDescricaoOKR('');
      setCategoriaOKR('');
      setDataInicioOKR('');
      setDataFimOKR('');
      setIsCreateOKRDialogOpen(false);

      toast.success('Objetivo criado com sucesso!');
    } catch (err: any) {
      console.error('Error creating OKR:', err);
      toast.error('Erro ao criar objetivo. Por favor, tente novamente.');
    }
  };

  // Função para abrir dialog de edição de objetivo
  const handleEditarObjetivo = (okrId: string) => {
    const okr = (okrsPorEmployee[employeeSelecionado] || []).find(o => o.okrId === okrId);
    if (!okr) return;

    setEditingOKRId(okrId);
    setEditObjetivoOKR(okr.objective || '');
    setEditDescricaoOKR(okr.description || '');
    setEditCategoriaOKR(okr.category || '');
    setEditDataInicioOKR(okr.period?.startDate || '');
    setEditDataFimOKR(okr.period?.endDate || '');
    setIsEditOKRDialogOpen(true);
  };

  // Função para salvar edição do objetivo
  const handleSalvarEdicaoObjetivo = async () => {
    if (!tenantDomain || !editingOKRId) return;

    // Validation
    if (!editObjetivoOKR || editObjetivoOKR.length < 10) {
      toast.error('O objetivo deve ter pelo menos 10 caracteres');
      return;
    }

    if (!editDataInicioOKR || !editDataFimOKR) {
      toast.error('Por favor, informe as datas de início e fim');
      return;
    }

    if (new Date(editDataFimOKR) <= new Date(editDataInicioOKR)) {
      toast.error('A data de fim deve ser posterior à data de início');
      return;
    }

    try {
      // Update OKR via API
      const updatedOKR = await updateOKR(tenantDomain, editingOKRId, {
        objective: editObjetivoOKR,
        description: editDescricaoOKR || undefined,
        category: editCategoriaOKR || undefined,
        period: {
          startDate: editDataInicioOKR,
          endDate: editDataFimOKR
        }
      });

      // Update local state with updated OKR
      setOkrsPorEmployee(prev => ({
        ...prev,
        [employeeSelecionado]: (prev[employeeSelecionado] || []).map(okr =>
          okr.okrId === editingOKRId ? updatedOKR : okr
        )
      }));

      // Clear form and close dialog
      setEditingOKRId(null);
      setEditObjetivoOKR('');
      setEditDescricaoOKR('');
      setEditCategoriaOKR('');
      setEditDataInicioOKR('');
      setEditDataFimOKR('');
      setIsEditOKRDialogOpen(false);

      toast.success('Objetivo atualizado com sucesso!');
    } catch (err: any) {
      console.error('Error updating OKR:', err);
      toast.error('Erro ao atualizar objetivo. Por favor, tente novamente.');
    }
  };

  // Delete OKR handler
  const handleDeleteOKR = async (okrId: string) => {
    if (!tenantDomain) return;

    const confirmed = window.confirm(
      'Tem certeza que deseja excluir este objetivo? Todos os resultados-chave associados também serão excluídos.'
    );
    if (!confirmed) return;

    try {
      await deleteOKR(tenantDomain, okrId);

      setOkrsPorEmployee(prev => ({
        ...prev,
        [employeeSelecionado]: (prev[employeeSelecionado] || []).filter(o => o.okrId !== okrId)
      }));

      setKeyResultsByOKR(prev => {
        const next = { ...prev };
        delete next[okrId];
        return next;
      });

      toast.success('Objetivo excluído com sucesso!');
    } catch (err) {
      console.error('Error deleting OKR:', err);
      toast.error('Erro ao excluir objetivo.');
    }
  };

  // Delete Key Result handler
  const handleDeleteKR = async (okrId: string, krId: string) => {
    if (!tenantDomain) return;

    const confirmed = window.confirm('Tem certeza que deseja excluir este resultado-chave?');
    if (!confirmed) return;

    try {
      await deleteKeyResult(tenantDomain, krId);

      setKeyResultsByOKR(prev => ({
        ...prev,
        [okrId]: (prev[okrId] || []).filter(kr => kr.krId !== krId)
      }));

      toast.success('Resultado-chave excluído!');
    } catch (err) {
      console.error('Error deleting KR:', err);
      toast.error('Erro ao excluir resultado-chave.');
    }
  };

  // Open edit KR dialog
  const handleEditKR = (okrId: string, kr: KeyResult) => {
    setEditingKR({ krId: kr.krId, okrId });
    setEditNomeKR(kr.name);
    setEditDescricaoKR(kr.description || '');
    setEditDataInicioKR(kr.period?.startDate || '');
    setEditDataFimKR(kr.period?.endDate || '');
    setEditPesoKR(kr.weight != null ? String(kr.weight) : '');
    setIsEditKRDialogOpen(true);
  };

  // Save edited KR
  const handleSalvarEdicaoKR = async () => {
    if (!tenantDomain || !editingKR) return;

    try {
      const updatedKR = await updateKeyResult(tenantDomain, editingKR.krId, {
        name: editNomeKR,
        description: editDescricaoKR || undefined,
        period: editDataInicioKR && editDataFimKR ? {
          startDate: editDataInicioKR,
          endDate: editDataFimKR
        } : undefined,
        weight: editPesoKR ? parseInt(editPesoKR, 10) : undefined,
      });

      setKeyResultsByOKR(prev => ({
        ...prev,
        [editingKR.okrId]: (prev[editingKR.okrId] || []).map(kr =>
          kr.krId === editingKR.krId ? updatedKR : kr
        )
      }));

      setIsEditKRDialogOpen(false);
      setEditingKR(null);
      toast.success('Resultado-chave atualizado!');
    } catch (err) {
      console.error('Error updating KR:', err);
      toast.error('Erro ao atualizar resultado-chave.');
    }
  };

  // Obter KRs de uma meta específica - now uses real backend data
  const getKRsPorMeta = (metaId: string): KeyResult[] => {
    return keyResultsByOKR[metaId] || [];
  };

  // Helper to get employee name from ownerId
  const getEmployeeName = (employeeId?: string | null): string => {
    if (!employeeId) return 'Não atribuído';
    const employee = realEmployees.find(emp => emp.employeeId === employeeId);
    return employee?.personalInfo.name || 'Desconhecido';
  };

  // Toggle expansão de KRs
  const toggleKRExpansion = (krId: string) => {
    setExpandedKRs(prev => ({
      ...prev,
      [krId]: !prev[krId]
    }));
  };

  // Obter badge de status - maps backend status to Portuguese UI
  const getStatusBadge = (status: KeyResult['status']) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-success/20 text-success border-success">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Concluído
        </Badge>;
      case 'in-progress':
        return <Badge className="bg-brand-gray/30/20 text-brand-gray border-brand-gray/30">
          <Clock className="h-3 w-3 mr-1" />
          Em andamento
        </Badge>;
      case 'at-risk':
        return <Badge className="bg-error/20 text-error border-error">
          <AlertCircle className="h-3 w-3 mr-1" />
          Em risco
        </Badge>;
      case 'not-started':
        return <Badge className="bg-brand-gray/30/20 text-brand-gray border-brand-gray/30">
          <Clock className="h-3 w-3 mr-1" />
          Não iniciado
        </Badge>;
      case 'cancelled':
        return <Badge className="bg-brand-gray/30/20 text-brand-gray border-brand-gray/30">
          <AlertCircle className="h-3 w-3 mr-1" />
          Cancelado
        </Badge>;
    }
  };

  const metasAgrupadas = getMetasAgrupadasPorCategoria();
  const employeeAtual = realEmployees.find(e => e.employeeId === employeeSelecionado);

  // Handle employee selection (for non-admin users)
  const handleSelectEmployee = (employeeId: string) => {
    setEmployeeSelecionado(employeeId);
    // Also set the name from realEmployees for consistency
    const employee = realEmployees.find(e => e.employeeId === employeeId);
    if (employee) {
      setEmployeeSelecionadoNome(employee.personalInfo.name);
    }
  };

  // Handle employee selection from autocomplete (for admin users)
  const handleAutocompleteSelect = (employeeId: string, employee?: { name: string }) => {
    setEmployeeSelecionado(employeeId);
    if (employee?.name) {
      setEmployeeSelecionadoNome(employee.name);
    }
  };

  // Handle opening scoring dialog
  const handleScoreKR = (kr: KeyResult) => {
    setScoringKR(kr);
    setIsScoringDialogOpen(true);
  };

  // Handle score submitted
  const handleScoreSubmitted = async () => {
    // Refresh KRs to get updated scores
    if (!tenantDomain || !employeeSelecionado) return;

    const okrs = okrsPorEmployee[employeeSelecionado] || [];
    if (okrs.length === 0) return;

    try {
      // Refetch Key Results to get updated scores
      const krPromises = okrs.map(async (okr) => {
        try {
          const response = await listKeyResultsByOKR(tenantDomain, okr.okrId, { limit: 100 });
          return { okrId: okr.okrId, keyResults: response.keyResults };
        } catch (err) {
          console.error(`Error fetching KRs for OKR ${okr.okrId}:`, err);
          return { okrId: okr.okrId, keyResults: [] };
        }
      });

      const results = await Promise.all(krPromises);

      // Build map of OKR ID -> Key Results
      const krMap: Record<string, KeyResult[]> = {};
      results.forEach(({ okrId, keyResults }) => {
        krMap[okrId] = keyResults;
      });

      setKeyResultsByOKR(prev => ({
        ...prev,
        ...krMap
      }));

      toast.success('Pontuação atualizada com sucesso!');
    } catch (err) {
      console.error('Error refreshing KRs:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm text-brand-gray">
        <span>Gestão de Pessoas</span>
        <span>/</span>
        <span className="text-white">Avaliação Individual</span>
      </div>

      {/* Cabeçalho */}
      <Card className="bg-brand-dark-light border-brand-gray/10">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center">
              <Target className="h-6 w-6 text-success" />
            </div>
            <div>
              <h1 className="text-white text-2xl">Avaliação Individual</h1>
              <p className="text-brand-gray">Definição e acompanhamento de objetivos e KRs individuais</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Selection Card - Enhanced */}
      {!loadingEmployees && realEmployees.length > 0 && (
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-dark-lighter rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-brand-gray" />
                </div>
                <div>
                  <Label className="text-brand-gray text-sm">Funcionário Selecionado</Label>
                  {employeeAtual ? (
                    <p className="text-white font-medium mt-1">
                      {user?.employee?.employeeId === employeeAtual.employeeId
                        ? 'Você'
                        : `${employeeAtual.personalInfo.name} ${''}`}
                    </p>
                  ) : (
                    <p className={employeeSelecionadoNome ? "text-white font-medium mt-1" : "text-brand-gray text-sm mt-1"}>
                      {employeeSelecionadoNome || 'Selecione um funcionário'}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* HR/Executives: Show EmployeeAutocomplete */}
                {showAutocomplete && (
                  <div className="w-80" data-testid="employee-autocomplete-container">
                    <EmployeeAutocomplete
                      onSelect={handleAutocompleteSelect}
                      navigateOnSelect={false}
                      placeholder="Buscar funcionário..."
                      data-testid="employee-autocomplete"
                    />
                  </div>
                )}

                {/* Regular User: Show dropdown only if more than 1 employee */}
                {!showAutocomplete && realEmployees.length > 1 && (
                  <div className="relative w-80" data-testid="employee-dropdown-container">
                    <Select value={employeeSelecionado} onValueChange={handleSelectEmployee}>
                      <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white hover:border-brand-gray/30" data-testid="employee-dropdown">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-brand-dark-lighter border-brand-gray/20 max-h-64 overflow-y-auto z-[9999]">
                        {realEmployees.map((employee) => (
                          <SelectItem
                            key={employee.employeeId}
                            value={employee.employeeId}
                            className="text-white hover:bg-brand-dark"
                          >
                            {user?.employee?.employeeId === employee.employeeId
                              ? `Você (${employee.personalInfo.name})`
                              : employee.personalInfo.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Create Objective Button - Admin Only */}
                {canCreateObjectives && employeeSelecionado && (
                  <Button
                    onClick={() => setIsCreateOKRDialogOpen(true)}
                    className="bg-success text-black hover:bg-success transition-all shadow-lg hover:shadow-green-500/20"
                    data-testid="create-objective-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Objetivo
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning Banner - Nenhum Funcionário */}
      {!loadingEmployees && realEmployees.length === 0 && (
        <Card className="bg-warning/20/20 border-warning">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-semibold text-sm">Nenhum funcionário cadastrado</h3>
                <p className="text-yellow-200 text-sm mt-1">
                  Para atribuir responsáveis aos KRs, cadastre funcionários na área de Gestão de Pessoas.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State - No objectives assigned */}
      {!loadingEmployees && !loadingOKRs && employeeSelecionado && (okrsPorEmployee[employeeSelecionado] || []).length === 0 && (
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-12">
            <div className="flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-success/20 rounded-full blur-2xl animate-pulse"></div>
                <Target className="h-16 w-16 text-success relative z-10" />
              </div>
              <h3 className="font-display text-2xl font-bold text-white mb-4">
                Nenhum objetivo definido para este funcionário
              </h3>
              <p className="text-brand-gray leading-relaxed mb-8">
                Este funcionário ainda não possui metas (OKRs) e Key Results atribuídos.
                {canCreateObjectives ? 'Use o botão "Criar Objetivo" acima para adicionar objetivos.' : 'Entre em contato com seu gestor para que ele configure os objetivos estratégicos e indicadores de desempenho.'}
              </p>
              <div className="flex items-center gap-3 px-6 py-4 bg-brand-dark-lighter/50 border border-brand-gray/20 rounded-lg">
                <Users className="h-5 w-5 text-brand-gray flex-shrink-0" />
                <p className="text-gray-300 text-sm text-left">
                  <strong>Próximo passo:</strong> Solicite ao seu gestor que adicione as metas
                  e objetivos para <strong>{employeeAtual
                    ? (user?.employee?.employeeId === employeeAtual.employeeId ? 'você' : `${employeeAtual.personalInfo.name} ${''}`)
                    : 'este funcionário'}</strong>.
                </p>
              </div>
              <div className="mt-8 text-sm text-brand-gray">
                Dica: Após a definição das metas, você poderá acompanhar seu progresso e gerenciar seus Key Results aqui
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estatísticas Rápidas */}
      {employeeSelecionado && (okrsPorEmployee[employeeSelecionado] || []).length > 0 && (() => {
        // Calculate all KRs for the selected employee
        const okrs = okrsPorEmployee[employeeSelecionado] || [];
        const allKRs = okrs.flatMap(okr => keyResultsByOKR[okr.okrId] || []);

        return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Total Objectives */}
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 bg-brand-dark-lighter rounded-lg flex items-center justify-center">
                  <Target className="h-5 w-5 text-brand-gray" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{okrs.length}</p>
              <p className="text-brand-gray text-sm mt-1">Objetivos Totais</p>
            </CardContent>
          </Card>

          {/* Total KRs */}
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{allKRs.length}</p>
              <p className="text-brand-gray text-sm mt-1">Key Results</p>
            </CardContent>
          </Card>

          {/* Completed KRs with Progress */}
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-success" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">
                {allKRs.filter(kr => kr.status === 'completed').length}
              </p>
              <p className="text-brand-gray text-sm mt-1">Concluídos</p>
              <div className="flex items-center gap-2 mt-3">
                <div className="h-1.5 bg-brand-dark-lighter rounded-full flex-1">
                  <div
                    className="h-full bg-success rounded-full transition-all duration-300"
                    style={{
                      width: `${allKRs.length > 0 ? (allKRs.filter(kr => kr.status === 'completed').length / allKRs.length * 100) : 0}%`
                    }}
                  />
                </div>
                <span className="text-xs text-brand-gray min-w-[3rem] text-right">
                  {allKRs.length > 0 ? Math.round(allKRs.filter(kr => kr.status === 'completed').length / allKRs.length * 100) : 0}%
                </span>
              </div>
            </CardContent>
          </Card>

          {/* At Risk KRs */}
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 bg-error/20 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-error" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">
                {allKRs.filter(kr => kr.status === 'at-risk').length}
              </p>
              <p className="text-brand-gray text-sm mt-1">Em Risco</p>
            </CardContent>
          </Card>
        </div>
        );
      })()}

      {/* Metas por Categoria */}
      {employeeSelecionado && (okrsPorEmployee[employeeSelecionado] || []).length > 0 && (
      <div className="space-y-6">
        {Object.entries(metasAgrupadas).map(([categoria, metas]) => {
          const IconCategoria = getCategoriaIcon(categoria);
          
          return (
            <Card key={categoria} className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                      <IconCategoria className="h-5 w-5 text-success" />
                    </div>
                    <div>
                      <CardTitle className="text-white">{categoria}</CardTitle>
                      <p className="text-brand-gray text-sm mt-1">
                        {metas.length} {metas.length === 1 ? 'meta' : 'metas'} definida{metas.length === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-brand-dark-lighter text-gray-300 border-brand-gray/20">
                    {employeeAtual
                      ? (user?.employee?.employeeId === employeeAtual.employeeId
                          ? 'Você'
                          : `${employeeAtual.personalInfo.name} ${''}`)
                      : 'Funcionário'}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {metas.map((meta) => {
                  const krsDestaMeta = getKRsPorMeta(meta.id);
                  
                  return (
                    <div key={meta.id} className="bg-brand-dark-light/50 border border-brand-gray/10 rounded-lg p-4">
                      {/* Meta */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-2 h-2 bg-success rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-white font-medium flex-1">{meta.titulo}</p>
                        {canCreateObjectives && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditarObjetivo(meta.id)}
                              className="text-brand-gray hover:text-white hover:bg-brand-dark-lighter"
                              title="Editar objetivo"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteOKR(meta.id)}
                              className="text-error hover:text-error hover:bg-error/10"
                              title="Excluir objetivo"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleAdicionarKR(meta.id)}
                              className="bg-success text-black hover:bg-success flex items-center gap-1"
                            >
                              <Plus className="h-3 w-3" />
                              Adicionar KR
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Lista de KRs ou Empty State */}
                      {krsDestaMeta.length > 0 ? (
                        <div className="mt-4 space-y-2 border-t border-brand-gray/10 pt-4">
                          <p className="text-brand-gray text-sm mb-3">
                            Key Results ({krsDestaMeta.length})
                          </p>

                          {krsDestaMeta.map((kr) => (
                            <div key={kr.krId} className="bg-brand-dark-light rounded-lg p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-1">
                                  <div className="flex items-center gap-2">
                                    {getStatusBadge(kr.status)}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-medium">{kr.name}</p>
                                    <div className="flex items-center gap-4 mt-1 text-xs text-brand-gray">
                                      {kr.period && (
                                        <span className="flex items-center gap-1">
                                          <Calendar className="h-3 w-3" />
                                          {new Date(kr.period.startDate).toLocaleDateString('pt-BR')} - {new Date(kr.period.endDate).toLocaleDateString('pt-BR')}
                                        </span>
                                      )}
                                      <span className="flex items-center gap-1">
                                        <Users className="h-3 w-3" />
                                        {getEmployeeName(kr.ownerId)}
                                      </span>
                                      <Badge variant="outline" className="text-xs">
                                        Peso: {kr.weight}
                                      </Badge>
                                      {(kr as any).averageScore !== null && (kr as any).averageScore !== undefined && (
                                        <span className="flex items-center gap-1 text-warning">
                                          <Star className="h-3 w-3 fill-yellow-400" />
                                          {(kr as any).averageScore.toFixed(1)}/5
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleKRExpansion(kr.krId)}
                                    className="text-brand-gray hover:text-white"
                                  >
                                    {expandedKRs[kr.krId] ? (
                                      <ChevronUp className="h-4 w-4" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4" />
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleScoreKR(kr)}
                                    className="text-warning hover:text-yellow-300 hover:bg-warning/10"
                                    title="Avaliar KR"
                                  >
                                    <Star className="h-4 w-4" />
                                  </Button>
                                  {canCreateObjectives && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEditKR(meta.id, kr)}
                                        className="text-brand-gray hover:text-white hover:bg-brand-dark-lighter"
                                        title="Editar resultado-chave"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteKR(meta.id, kr.krId)}
                                        className="text-error hover:text-error hover:bg-error/10"
                                        title="Excluir resultado-chave"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Descrição expandível */}
                              {expandedKRs[kr.krId] && kr.description && (
                                <div className="mt-3 pt-3 border-t border-brand-gray/10">
                                  <p className="text-brand-gray text-sm">{kr.description}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-4 border-t border-brand-gray/10 pt-4">
                          <div className="flex flex-col items-center justify-center py-8 px-4 bg-brand-dark-light/50 rounded-lg border-2 border-dashed border-brand-gray/10">
                            <Target className="h-10 w-10 text-brand-gray mb-3" />
                            <p className="text-brand-gray text-sm text-center mb-3">
                              Nenhum Key Result definido para esta meta
                            </p>
                            <Button
                              size="sm"
                              onClick={() => handleAdicionarKR(meta.id)}
                              className="bg-success text-black hover:bg-success"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Adicionar Primeiro KR
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {/* Dialog para Adicionar KR */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Adicionar Key Result (KR)</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Meta pré-selecionada */}
            <div>
              <Label className="text-brand-gray">Meta Vinculada</Label>
              <div className="bg-brand-dark-lighter p-3 rounded-lg mt-2">
                <p className="text-gray-200 text-sm">
                  {(okrsPorEmployee[employeeSelecionado] || []).find(okr => okr.okrId === metaSelecionada)?.objective}
                </p>
              </div>
            </div>

            {/* Nome do KR */}
            <div>
              <Label htmlFor="nomeKR" className="text-brand-gray">Nome do KR *</Label>
              <Input
                id="nomeKR"
                value={nomeKR}
                onChange={(e) => setNomeKR(e.target.value)}
                placeholder="Ex: Implementar dashboard de rentabilidade"
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
              />
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="descricaoKR" className="text-brand-gray">Descrição Detalhada *</Label>
              <Textarea
                id="descricaoKR"
                value={descricaoKR}
                onChange={(e) => setDescricaoKR(e.target.value)}
                placeholder="Descreva o que será feito, como será medido e o impacto esperado..."
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2 min-h-[100px]"
              />
            </div>

            {/* Período */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dataInicio" className="text-brand-gray">Data de Início *</Label>
                <Input
                  id="dataInicio"
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
                />
              </div>
              <div>
                <Label htmlFor="dataFim" className="text-brand-gray">Data de Término *</Label>
                <Input
                  id="dataFim"
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
                />
              </div>
            </div>

            {/* Responsável e Peso */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="responsavel" className="text-brand-gray">Responsável *</Label>
                <div className="bg-brand-dark-lighter border border-brand-gray/20 rounded-md px-3 py-2 mt-2">
                  <p className="text-white text-sm">
                    {responsavel || employeeAtual?.personalInfo.name || employeeSelecionadoNome || 'Não definido'}
                  </p>
                </div>
                <p className="text-xs text-brand-gray mt-1">O responsável é o funcionário selecionado</p>
              </div>
              <div>
                <Label htmlFor="peso" className="text-brand-gray">Peso / Prioridade</Label>
                <Select value={peso} onValueChange={setPeso}>
                  <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2">
                    <SelectValue placeholder="Selecione o peso" />
                  </SelectTrigger>
                  <SelectContent className="bg-brand-dark-lighter border-brand-gray/20">
                    <SelectItem value="25" className="text-white">Baixo (25)</SelectItem>
                    <SelectItem value="50" className="text-white">Médio (50)</SelectItem>
                    <SelectItem value="75" className="text-white">Alto (75)</SelectItem>
                    <SelectItem value="100" className="text-white">Crítico (100)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ações */}
            <div className="flex justify-end gap-3 pt-4 border-t border-brand-gray/10">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-brand-gray/20 text-gray-300 hover:bg-brand-dark-lighter"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSalvarKR}
                disabled={!nomeKR || !descricaoKR || !dataInicio || !dataFim || !responsavel}
                className="bg-success text-black hover:bg-success"
              >
                Salvar KR
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para Editar KR */}
      <Dialog open={isEditKRDialogOpen} onOpenChange={setIsEditKRDialogOpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Editar Key Result (KR)</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="editNomeKR" className="text-brand-gray">Nome do KR *</Label>
              <Input
                id="editNomeKR"
                value={editNomeKR}
                onChange={(e) => setEditNomeKR(e.target.value)}
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
              />
            </div>

            <div>
              <Label htmlFor="editDescricaoKR" className="text-brand-gray">Descrição Detalhada</Label>
              <Textarea
                id="editDescricaoKR"
                value={editDescricaoKR}
                onChange={(e) => setEditDescricaoKR(e.target.value)}
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2 min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editDataInicioKR" className="text-brand-gray">Data de Início</Label>
                <Input
                  id="editDataInicioKR"
                  type="date"
                  value={editDataInicioKR}
                  onChange={(e) => setEditDataInicioKR(e.target.value)}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
                />
              </div>
              <div>
                <Label htmlFor="editDataFimKR" className="text-brand-gray">Data de Término</Label>
                <Input
                  id="editDataFimKR"
                  type="date"
                  value={editDataFimKR}
                  onChange={(e) => setEditDataFimKR(e.target.value)}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="editPesoKR" className="text-brand-gray">Peso / Prioridade</Label>
              <Select value={editPesoKR} onValueChange={setEditPesoKR}>
                <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2">
                  <SelectValue placeholder="Selecione o peso" />
                </SelectTrigger>
                <SelectContent className="bg-brand-dark-lighter border-brand-gray/20">
                  <SelectItem value="25" className="text-white">Baixo (25)</SelectItem>
                  <SelectItem value="50" className="text-white">Médio (50)</SelectItem>
                  <SelectItem value="75" className="text-white">Alto (75)</SelectItem>
                  <SelectItem value="100" className="text-white">Crítico (100)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-brand-gray/10">
              <Button
                variant="outline"
                onClick={() => setIsEditKRDialogOpen(false)}
                className="border-brand-gray/20 text-gray-300 hover:bg-brand-dark-lighter"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSalvarEdicaoKR}
                disabled={!editNomeKR}
                className="bg-success text-black hover:bg-success"
              >
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para Criar Objetivo (OKR) - Admin Only */}
      <Dialog open={isCreateOKRDialogOpen} onOpenChange={setIsCreateOKRDialogOpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Criar Novo Objetivo</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Funcionário selecionado */}
            <div>
              <Label className="text-brand-gray">Funcionário</Label>
              <div className="bg-brand-dark-lighter p-3 rounded-lg mt-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-success" />
                  <p className="text-gray-200 text-sm">
                    {employeeAtual
                      ? (user?.employee?.employeeId === employeeAtual.employeeId
                          ? 'Você'
                          : employeeAtual.personalInfo.name)
                      : (employeeSelecionadoNome || 'Não selecionado')}
                  </p>
                </div>
              </div>
            </div>

            {/* Objetivo */}
            <div>
              <Label htmlFor="objetivoOKR" className="text-brand-gray">Objetivo *</Label>
              <Input
                id="objetivoOKR"
                value={objetivoOKR}
                onChange={(e) => setObjetivoOKR(e.target.value)}
                placeholder="Ex: Aumentar a satisfação dos clientes em 20%"
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
              />
              <p className="text-xs text-brand-gray mt-1">Mínimo 10 caracteres</p>
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="descricaoOKR" className="text-brand-gray">Descrição (opcional)</Label>
              <Textarea
                id="descricaoOKR"
                value={descricaoOKR}
                onChange={(e) => setDescricaoOKR(e.target.value)}
                placeholder="Descreva mais detalhes sobre este objetivo..."
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2 min-h-[80px]"
              />
            </div>

            {/* Categoria */}
            <div>
              <Label htmlFor="categoriaOKR" className="text-brand-gray">Categoria (opcional)</Label>
              <Input
                id="categoriaOKR"
                value={categoriaOKR}
                onChange={(e) => setCategoriaOKR(e.target.value)}
                placeholder="Ex: Gestão de Eventos, Crescimento de Receita, etc."
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
              />
            </div>

            {/* Período */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dataInicioOKR" className="text-brand-gray">Data de Início *</Label>
                <Input
                  id="dataInicioOKR"
                  type="date"
                  value={dataInicioOKR}
                  onChange={(e) => setDataInicioOKR(e.target.value)}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
                />
              </div>
              <div>
                <Label htmlFor="dataFimOKR" className="text-brand-gray">Data de Término *</Label>
                <Input
                  id="dataFimOKR"
                  type="date"
                  value={dataFimOKR}
                  onChange={(e) => setDataFimOKR(e.target.value)}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
                />
              </div>
            </div>

            {/* Ações */}
            <div className="flex justify-end gap-3 pt-4 border-t border-brand-gray/10">
              <Button
                variant="outline"
                onClick={() => setIsCreateOKRDialogOpen(false)}
                className="border-brand-gray/20 text-gray-300 hover:bg-brand-dark-lighter"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCriarObjetivo}
                disabled={!objetivoOKR || objetivoOKR.length < 10 || !dataInicioOKR || !dataFimOKR}
                className="bg-success text-black hover:bg-success"
              >
                Criar Objetivo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para Editar Objetivo (OKR) */}
      <Dialog open={isEditOKRDialogOpen} onOpenChange={setIsEditOKRDialogOpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Editar Objetivo</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Funcionário selecionado */}
            <div>
              <Label className="text-brand-gray">Funcionário</Label>
              <div className="bg-brand-dark-lighter p-3 rounded-lg mt-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-success" />
                  <p className="text-gray-200 text-sm">
                    {employeeAtual
                      ? (user?.employee?.employeeId === employeeAtual.employeeId
                          ? 'Você'
                          : `${employeeAtual.personalInfo.name}`)
                      : 'Não selecionado'}
                  </p>
                </div>
              </div>
            </div>

            {/* Objetivo */}
            <div>
              <Label htmlFor="editObjetivoOKR" className="text-brand-gray">Objetivo *</Label>
              <Input
                id="editObjetivoOKR"
                value={editObjetivoOKR}
                onChange={(e) => setEditObjetivoOKR(e.target.value)}
                placeholder="Ex: Aumentar a satisfação dos clientes em 20%"
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
              />
              <p className="text-xs text-brand-gray mt-1">Mínimo 10 caracteres</p>
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="editDescricaoOKR" className="text-brand-gray">Descrição (opcional)</Label>
              <Textarea
                id="editDescricaoOKR"
                value={editDescricaoOKR}
                onChange={(e) => setEditDescricaoOKR(e.target.value)}
                placeholder="Descreva mais detalhes sobre este objetivo..."
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2 min-h-[80px]"
              />
            </div>

            {/* Categoria */}
            <div>
              <Label htmlFor="editCategoriaOKR" className="text-brand-gray">Categoria (opcional)</Label>
              <Input
                id="editCategoriaOKR"
                value={editCategoriaOKR}
                onChange={(e) => setEditCategoriaOKR(e.target.value)}
                placeholder="Ex: Gestão de Eventos, Crescimento de Receita, etc."
                className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
              />
            </div>

            {/* Período */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editDataInicioOKR" className="text-brand-gray">Data de Início *</Label>
                <Input
                  id="editDataInicioOKR"
                  type="date"
                  value={editDataInicioOKR}
                  onChange={(e) => setEditDataInicioOKR(e.target.value)}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
                />
              </div>
              <div>
                <Label htmlFor="editDataFimOKR" className="text-brand-gray">Data de Término *</Label>
                <Input
                  id="editDataFimOKR"
                  type="date"
                  value={editDataFimOKR}
                  onChange={(e) => setEditDataFimOKR(e.target.value)}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white mt-2"
                />
              </div>
            </div>

            {/* Ações */}
            <div className="flex justify-end gap-3 pt-4 border-t border-brand-gray/10">
              <Button
                variant="outline"
                onClick={() => setIsEditOKRDialogOpen(false)}
                className="border-brand-gray/20 text-gray-300 hover:bg-brand-dark-lighter"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSalvarEdicaoObjetivo}
                disabled={!editObjetivoOKR || editObjetivoOKR.length < 10 || !editDataInicioOKR || !editDataFimOKR}
                className="bg-success text-black hover:bg-success"
              >
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* KR Scoring Dialog */}
      {scoringKR && (
        <KRScoringDialog
          krId={scoringKR.krId}
          krName={scoringKR.name}
          isOpen={isScoringDialogOpen}
          onClose={() => {
            setIsScoringDialogOpen(false);
            setScoringKR(null);
          }}
          onScoreSubmitted={handleScoreSubmitted}
        />
      )}
    </div>
  );
};

export default AvaliacaoIndividual;